// StoriesRail — IG-style horizontal scroll of circular avatars at the top of /feed.
// Renders one tile per author with at least one un-expired (kind="story") post in
// the last 24h. Unseen stories get a gradient ring; seen ones go grayscale.
// Tap a tile → opens a fullscreen viewer that cycles through that author's stories
// and POSTs /api/stories/{id}/seen after each one shown.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X, ChevronLeft, ChevronRight, VolumeX, Eye } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import StoriesUploader from "./StoriesUploader";
import InteractorsModal from "./InteractorsModal";

export default function StoriesRail() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tiles, setTiles] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [uploaderOpen, setUploaderOpen] = useState(false);

  const load = () => api.get("/stories").then(({ data }) => setTiles(data.items || [])).catch(() => {});
  useEffect(() => {
    load();
    const onRefresh = () => load();
    window.addEventListener("nexus:stories-refresh", onRefresh);
    return () => window.removeEventListener("nexus:stories-refresh", onRefresh);
  }, []);

  const open = async (tile) => {
    // Fetch full story list for that user
    try {
      const { data } = await api.get(`/stories/${tile.user_id}`);
      setViewing({ tile, stories: data.items || [], idx: 0 });
    } catch { /* noop */ }
  };

  useEffect(() => {
    // Lets other components (e.g. the rectangle preview cards on the Profile
    // page) open this same viewer without duplicating the fetch/viewer logic.
    const onOpenRequest = (e) => { if (e.detail) open(e.detail); };
    window.addEventListener("nexus:story-open-request", onOpenRequest);
    return () => window.removeEventListener("nexus:story-open-request", onOpenRequest);
  }, []);

  return (
    <>
      <div className="relative bg-black/40 backdrop-blur-md border-b border-white/5 px-3 py-3" data-testid="stories-rail">
        <div className="flex gap-3 overflow-x-auto no-scrollbar items-center">
          {/* "Your story" tile — always first, opens uploader */}
          {user && (
            <button onClick={() => setUploaderOpen(true)} className="flex flex-col items-center gap-1 shrink-0" data-testid="story-add-tile">
              <div className="relative w-14 h-14 rounded-full ring-2 ring-white/20 bg-white/5 flex items-center justify-center">
                <img src={user.avatar} alt="" className="w-full h-full rounded-full object-cover opacity-70" />
                <span className="absolute -bottom-1 -right-1 bg-nexus-gold rounded-full w-5 h-5 text-black flex items-center justify-center text-[10px] font-bold ring-2 ring-black">
                  <Plus className="w-3 h-3" />
                </span>
              </div>
              <span className="text-[9px] tracking-wider text-white/70 font-display max-w-[60px] truncate">Your story</span>
            </button>
          )}
          {tiles.map((t) => (
            <button key={t.user_id} onClick={() => open(t)} className="flex flex-col items-center gap-1 shrink-0 group" data-testid={`story-tile-${t.user_id}`}>
              <div className={`w-14 h-14 rounded-full p-0.5 transition-transform group-active:scale-95 ${
                t.has_unseen
                  ? "bg-gradient-to-tr from-nexus-pink via-nexus-gold to-nexus-cyan"
                  : "ring-2 ring-white/15"
              }`}>
                <img src={t.user_avatar || `https://api.dicebear.com/7.x/identicon/svg?seed=${t.user_id}`} alt="" className={`w-full h-full rounded-full object-cover ring-2 ring-black ${t.has_unseen ? "" : "grayscale opacity-70"}`} />
              </div>
              <span className="text-[9px] tracking-wider text-white/80 font-display max-w-[60px] truncate">{t.user_name}</span>
            </button>
          ))}
          {tiles.length === 0 && !user && (
            <div className="text-[10px] text-white/40 px-2">No stories in the last 24h — <button onClick={() => nav("/auth")} className="underline text-nexus-cyan">sign in</button> to post one.</div>
          )}
        </div>
      </div>

      <StoriesUploader open={uploaderOpen} onClose={() => setUploaderOpen(false)} onPosted={load} />

      {viewing && <StoryViewer state={viewing} setState={setViewing} />}
    </>
  );
}

function StoryViewer({ state, setState }) {
  const { tile, stories, idx } = state;
  const { user: me } = useAuth();
  const story = stories[idx];
  const videoRef = useRef(null);
  const [needsUnmuteTap, setNeedsUnmuteTap] = useState(false);
  const [viewersOpen, setViewersOpen] = useState(false);
  const [liveDuration, setLiveDuration] = useState(null);
  // Only the story author + platform owner can see the viewer list — matches
  // Instagram/FB story-viewer privacy. Feb 2026.
  const canSeeViewers = me && (me.id === tile.user_id || me.role === "owner");
  // Play for the clip's real length. duration_seconds is captured once at upload
  // time via a <video> metadata probe, but that probe unreliably reports 0 for
  // some mobile-recorded files (a known browser quirk -- duration isn't known
  // until playback actually starts). liveDuration, read directly off this video
  // element once it starts playing, overrides that stored estimate when available
  // so playback always matches the real clip length instead of falling back to 6s.
  const durationS = Math.min(120, Math.max(3, Math.round(liveDuration || story?.duration_seconds || 6)));

  useEffect(() => { setLiveDuration(null); }, [idx]);

  useEffect(() => {
    if (!story) return;
    api.post(`/stories/${story.id}/seen`).catch(() => {});
    if (viewersOpen) return;   // pause auto-advance while the viewer-list modal is open
    const t = setTimeout(() => {
      if (idx + 1 < stories.length) setState({ ...state, idx: idx + 1 });
      else setState(null);
    }, durationS * 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, viewersOpen, durationS]);

  // Try to play WITH sound first (opening a story is itself a user gesture in
  // most browsers, so this usually works). If the browser blocks unmuted
  // autoplay anyway, fall back to muted so playback isn't blocked entirely,
  // and let the viewer tap to turn sound on.
  useEffect(() => {
    setNeedsUnmuteTap(false);
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    const p = v.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => {
        v.muted = true;
        setNeedsUnmuteTap(true);
        v.play().catch(() => {});
      });
    }
  }, [idx]);

  if (!story) { setState(null); return null; }
  const back = () => idx > 0 ? setState({ ...state, idx: idx - 1 }) : setState(null);
  const fwd  = () => idx + 1 < stories.length ? setState({ ...state, idx: idx + 1 }) : setState(null);

  return (
    <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center" data-testid="story-viewer" onClick={fwd}>
      <button onClick={(e) => { e.stopPropagation(); setState(null); }} className="absolute top-3 right-3 bg-black/60 hover:bg-black/75 p-2 rounded-full z-10"><X className="w-5 h-5 text-white" style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.8))" }} /></button>
      <button onClick={(e) => { e.stopPropagation(); back(); }} className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/75 p-2 rounded-full z-10"><ChevronLeft className="w-5 h-5 text-white" style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.8))" }} /></button>
      <button onClick={(e) => { e.stopPropagation(); fwd(); }} className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/75 p-2 rounded-full z-10"><ChevronRight className="w-5 h-5 text-white" style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.8))" }} /></button>

      <div className="absolute top-3 left-3 right-16 flex gap-1 z-10">
        {stories.map((_, i) => (
          <div key={i} className="flex-1 h-0.5 bg-white/20 rounded-full overflow-hidden">
            <div className={`h-full bg-white ${i < idx ? "w-full" : i !== idx ? "w-0" : ""}`} style={i === idx ? { animation: `storyfill ${durationS}s linear forwards` } : {}} />
          </div>
        ))}
      </div>

      <div className="relative max-w-md w-full aspect-[9/16] max-h-[90vh] bg-nexus-obsidian overflow-hidden">
        {story.media_url ? (
          (story.kind === "story" && (story.media_url || "").match(/\.(mp4|webm|mov)/i))
            ? <video ref={videoRef} src={story.media_url} autoPlay playsInline className="w-full h-full object-cover"
                onLoadedMetadata={(e) => {
                  const d = e.currentTarget.duration;
                  if (Number.isFinite(d) && d > 0) setLiveDuration(d);
                }}
              />
            : <img src={story.media_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white text-lg p-6 text-center bg-gradient-to-br from-nexus-pink via-nexus-magenta to-nexus-cyan">{story.content}</div>
        )}
        <div className="absolute top-6 left-3 right-3 flex items-center gap-2 z-10">
          <img src={tile.user_avatar} alt="" className="w-8 h-8 rounded-full ring-2 ring-white" />
          <div className="text-white text-sm font-display">{tile.user_name}</div>
        </div>
        {story.content && story.media_url && (
          <div className="absolute bottom-4 left-3 right-3 text-white text-sm bg-black/40 backdrop-blur-sm rounded-2xl px-3 py-2">{story.content}</div>
        )}
        {canSeeViewers && (
          <button
            onClick={(e) => { e.stopPropagation(); setViewersOpen(true); }}
            className="absolute bottom-4 left-3 z-10 bg-black/60 backdrop-blur px-3 py-1.5 rounded-full flex items-center gap-1.5"
            style={{ color: "#fff" }}
            data-testid="story-viewers-open"
            title="Who viewed this story"
          >
            <Eye className="w-3 h-3" style={{ color: "#fff" }} />
            <span className="text-[10px] font-display tracking-wider" style={{ color: "#fff" }}>Viewers</span>
          </button>
        )}
        {needsUnmuteTap && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              const v = videoRef.current;
              if (v) { v.muted = false; v.play().catch(() => {}); }
              setNeedsUnmuteTap(false);
            }}
            className="absolute bottom-4 right-3 z-10 bg-black/60 backdrop-blur px-3 py-1.5 rounded-full text-white text-[10px] flex items-center gap-1.5"
            data-testid="story-unmute"
          >
            <VolumeX className="w-3 h-3" /> Tap for sound
          </button>
        )}
      </div>
      {viewersOpen && (
        <div onClick={(e) => e.stopPropagation()}>
          <InteractorsModal
            postId={story.id}
            initialKind="view"
            fetchUrl={`/stories/${story.id}/viewers`}
            title="Story viewers"
            onClose={() => setViewersOpen(false)}
          />
        </div>
      )}
      <style>{`@keyframes storyfill { from { width: 0% } to { width: 100% } }`}</style>
    </div>
  );
}
